import {
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  _adapters,
  Decimation,
} from 'chart.js';
import { StdDateAdapter } from 'chartjs-adapter-date-std';
import { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';

import type { GraphableChartProps, GraphableDataset } from './types';

// Setup Chart.js adapters (copied from Remote Control)
// eslint-disable-next-line no-underscore-dangle
_adapters._date.override(StdDateAdapter.chartJsStandardAdapter());

ChartJS.register(
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Decimation,
);

const COL_COLOR_MAPPING: Record<string, string> = {
  PhaseACurrentInAmps: `hsl(${30 * 46}deg, 70%, 60%)`,
  PhaseBCurrentInAmps: `hsl(${30 * 47}deg, 70%, 60%)`,
  PhaseCCurrentInAmps: `hsl(${30 * 48}deg, 70%, 60%)`,
  MotorIq: `hsl(${30 * 27}deg, 70%, 60%)`,
  MotorId: `hsl(${30 * 28}deg, 70%, 60%)`,
  position: `hsl(${30 * 69}deg, 70%, 60%)`,
  velocity: `hsl(${30 * 13}deg, 70%, 60%)`,
};

export function GraphableChart({
  dataSource,
  selectedGraphableTypes,
}: GraphableChartProps) {
  const chartRef = useRef<ChartJS<'line'>>(null);

  const [datasets, setDatasets] = useState<GraphableDataset[]>([]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !chart.data) return;

    // Update chart datasets
    const newDatasets: GraphableDataset[] = [];

    selectedGraphableTypes.forEach((graphableType, idx) => {
      if (
        dataSource.getAvailableGraphableTypes().includes(graphableType) ===
        false
      ) {
        return;
      }

      const data =
        dataSource.getData(graphableType).map((point) => ({
          x: point.timestamp,
          y: point.value,
        })) || [];

      const color =
        COL_COLOR_MAPPING[graphableType] || `hsl(${30 * idx}deg, 70%, 60%)`;

      newDatasets.push({
        type: 'line',
        label: graphableType,
        data,
        borderColor: color,
        backgroundColor: color,
        pointStyle: false,
      });
    });

    setDatasets(newDatasets);
    chart.data.datasets = newDatasets;
  }, [dataSource, selectedGraphableTypes]);

  return (
    <div className="tw-flex tw-flex-row tw-h-full tw-items-stretch">
      <Line
        ref={chartRef}
        height={400}
        options={{
          animation: false,
          scales: {
            x: {
              type: 'time',
              display: true,
            },
            y: {
              suggestedMax: 1,
              suggestedMin: -1,
            },
          },
          plugins: {
            decimation: {
              enabled: true,
              algorithm: 'min-max',
            },
            legend: {
              position: 'top',
              fullSize: true,
              labels: {
                font: {
                  size: 16,
                },
              },
            },
          },
          aspectRatio: undefined,
          maintainAspectRatio: false,
        }}
        data={{
          datasets,
        }}
      />
    </div>
  );
}
